// components/ReportModal.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Flag } from "lucide-react";
import { Button } from  "../../components/ui/Button";
import { CategoryEnum } from  "../../modules/shared/model";

interface ReportModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (reportData: any) => void;
  newTicket: any;
  setNewTicket: (ticket: any) => void;
}

const ReportModal: React.FC<ReportModalProps> = ({
  show,
  onClose,
  onSubmit,
  newTicket,
  setNewTicket,
}) => {
  const { t } = useTranslation();
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl w-full max-w-2xl mx-4 overflow-hidden"
      >
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">{t("reportModal.title")}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(newTicket);
            }}
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("reportModal.subject")}
                </label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, subject: e.target.value })
                  }
                  className="w-full p-2 border rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("reportModal.description")}
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) =>
                    setNewTicket({ ...newTicket, description: e.target.value })
                  }
                  className="w-full p-2 border rounded-lg"
                  rows={4}
                  required
                  placeholder={t("reportModal.descriptionPlaceholder")}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("reportModal.priority")}
                </label>
                <select
                  value={newTicket.priority}
                  onChange={(e) =>
                    setNewTicket({
                      ...newTicket,
                      priority: e.target.value,
                    })
                  }
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="low">{t("reportModal.low")}</option>
                  <option value="medium">{t("reportModal.medium")}</option>
                  <option value="high">{t("reportModal.high")}</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {t("reportModal.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={!newTicket.subject || !newTicket.description}
              >
                <Flag className="w-4 h-4 mr-2" />
                {t("reportModal.submitReport")}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default ReportModal;